import styled, { css } from "styled-components"

const getColors = (campaign) => campaign && campaign.template && campaign.template.appearence && campaign.template.appearence.colors

const ClassBuilder = styled.div`
  font-style: inherit;
  ${props => {
    const colors = getColors(props.campaign)
    const c = props.campaign || {}
    // Three-tier fallback to handle all known backend formats:
    //
    //   Tier 1 — New design-token keys inside campaign.template.appearence.colors
    //            e.g. colors["brand-default"], colors["background-page"]
    //            Used by base_2026 / Gamification2026Template campaigns.
    //
    //   Tier 2 — Legacy field names stored INSIDE campaign.template.appearence.colors
    //            e.g. colors["footer_bg_color"], colors["header_bg_color"]
    //            Used when colors_scheme === "legacy" (middle-migration backends).
    //            This is the current VW/myrace (gamification_levels) structure.
    //
    //   Tier 3 — Flat fields at campaign root (oldest backends, pre-migration)
    //            e.g. campaign.footer_bg_color, campaign.color_primary
    //
    const brandDefault   = (colors && colors["brand-default"])    || (colors && colors["color_primary"])      || c.color_primary
    const brandHover     = (colors && colors["brand-hover"])       || (colors && colors["color_secondary"])    || c.color_secondary
    const brandPressed   = (colors && colors["brand-pressed"])     || (colors && colors["text_color"])         || c.text_color
    // Header nav links used header_text_color; sidebar/sub-nav used navbar_text_color
    const headerText     = (colors && colors["text-tertiary"])     || (colors && colors["header_text_color"])  || c.header_text_color
    const textTertiary   = (colors && colors["text-tertiary"])     || (colors && colors["navbar_text_color"])  || c.navbar_text_color
    const backgroundPage = (colors && colors["background-page"])   || (colors && colors["header_bg_color"])    || c.header_bg_color
    const brandHoverBg   = (colors && colors["brand-hover"])       || (colors && colors["header_hover_bg_color"]) || c.header_hover_bg_color
    const hoverColor     = (colors && colors["brand-default"])     || (colors && colors["header_hover_color"]) || c.header_hover_color
    // Footer has dedicated colour fields — must NOT collapse to header_bg_color.
    const footerBg       = (colors && colors["footer-background"]) || (colors && colors["footer_bg_color"])    || c.footer_bg_color   || backgroundPage
    const footerText     = (colors && colors["footer-text"])       || (colors && colors["footer_text_color"])  || c.footer_text_color  || textTertiary

    return props.campaign && css`
    .primary-text {
      color: ${brandDefault}
    }
    .text-color {
      color: ${brandPressed}
    }

    .secondary-text {
      color: ${brandHover}
    }
    .header-text {
      color: ${headerText}
    }
    .navbar-text {
      color: ${textTertiary}
    }
    .table-body-color {Color:#8E959B}

    .dropdown-item {#000000}

    .table-body-color-bg {
      background: #dae1e7
      fill: #dae1e7}

    .navbar-text-white {
      color: white
    }

    .primary-bg {
      background: ${brandDefault}
      fill: ${brandDefault}
    }

    .primary-fill {
      fill: ${brandDefault}
    }
    .secondary-fill {
      fill: ${brandHover}
    }
    .navbar-fill {
      fill: ${textTertiary}
    }
    .mish-fill {
      fill: ${textTertiary}
    }

    .primary-bg-after {
      &:after {
        background: ${brandDefault}
      }
    }

    .secondary-bg {
      background: ${brandHover}
      fill: ${brandHover}
    }

    .text-bg {
      background: ${brandPressed}
      fill: ${brandPressed}
    }

    .header-bg {
      background: ${backgroundPage}
    }

    .navbar-bg {
      background: ${textTertiary}
    }

    /* Target both classes on the footer element for specificity [0,2,0]
       to reliably override any SCSS defaults */
    .bgl-footer.footer {
      background: ${footerBg};
      color: ${footerText};
    }
    /* Keep the single-class selector as a fallback */
    .footer {
      background: ${footerBg};
      color: ${footerText};
    }

    .mish-bg {
      background: ${textTertiary}
      fill: ${textTertiary}
    }

    .border-mish {
      border-color: ${textTertiary}
    }

    .border-primary {
      border-color: ${brandDefault}
    }
    .border-secondary {
      border-color: ${brandHover}
    }
    .border-navbar {
      border-color: ${textTertiary}
    }

    .primary-border-color{
      border-color: ${brandDefault}
    }

    .half-gradient {
      background: linear-gradient(to bottom, ${brandDefault} 20%, #973031 50%, white 50%)
    }
    .navbar-campaign-link {
      position:relative;
      color: ${headerText};
      &:after {
        transition: background-color 300ms ease-in;
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: transparent;
      }
      &:visited {
        color: ${headerText};
      }
      &:hover {
        color: ${hoverColor};
        &:after {
          background: ${hoverColor};
        }
        path, circle, rect, line {
          stroke: ${hoverColor};
        }
      }
    }
    .campaign-link {
      position:relative;
      color: ${textTertiary};
      &:after {
        transition: background-color 300ms ease-in;
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: transparent;
      }
      &:visited {
        color: ${textTertiary};
      }
      &:hover {
        color: ${brandDefault};
        &:after {
          background: ${brandDefault};
        }
        path, circle, rect {
          stroke: ${brandDefault};
        }
      }
    }
    .navbar-active-link {
      position:relative;
      color: ${brandDefault}
      &:visited {
        color: ${brandDefault};
      }
      &:hover {
        color: ${brandDefault};
      }
      &:after {
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: ${brandDefault}
      }
    }

    .campaign-link-sub-navbar {
      position:relative;
      color: ${brandDefault};
      &:after {
        transition: background-color 300ms ease-in;
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: transparent;
      }
      &:visited {
        color: ${brandHover};
      }
      &:hover {
        color: ${brandDefault};
        &:after {
          background: ${brandDefault};
        }
        path, circle, rect {
          stroke: ${brandHover};
        }
      }
    }

    .svg-primary-fill {
      path, circle, rect {
        fill: ${brandDefault};
      }
    }
    .navbar-campaign-link-icon {
      color: ${headerText};
      background-color: ${backgroundPage};
      .navbar-campaign-link-icon__stroke {
        path, circle, rect {
          stroke: ${headerText};
        }
      }
      &:hover {
        span#notification-counter{
          color: ${hoverColor};
          background-color: ${brandHoverBg};
        }
        color: ${hoverColor};
        background-color: ${brandHoverBg};
        .navbar-campaign-link-icon__stroke {
          path, circle, rect {
            stroke: ${hoverColor};
            fill: ${hoverColor};
          }
        }

      }
    }
    .campaign-link-icon {
         path, circle, rect, polyline, line {
          stroke: ${headerText};
        }
      &:hover {
        path, circle, rect, polyline, line {
          stroke: ${brandDefault};
        }
      }
    }
    .campaign-drop-down-item {
      &:hover {
        & > path, & >  circle, & > rect, & > polyline, & > line {
          stroke: ${brandDefault};
        }
        color: ${brandDefault};
      }
    }
    .active-link {
      position:relative;
      &:visited {
        color: ${brandDefault};
      }
      &:hover {
        color: ${brandDefault};
      }
      &:after {
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: ${brandDefault}
      }
    }



    &__menu, __menu-list {
      border-radius: 0px;
      width: calc(100% - 1rem)
    }

    &__control {
      border-color: #8E959B;
      box-shadow: none;
      border-radius: 0px;
      &--is-focused, &--menu-is-open {
        border: 1px solid ${textTertiary};
        &:hover {
          border: 1px solid ${textTertiary};
        }
      }
    }

    &__option {
      width: calc(100% - 10px);
      margin: 0 5px;
      color: #8E959B;
      cursor: pointer;
      background: white;

      &--is-focused  {
        color: ${textTertiary};
        background: #FAFAFA;
      }

      &--is-selected {
        color: ${textTertiary};
        background: #FAFAFA;
      }

      &:active {
        background: ${textTertiary};
        background: ${brandPressed};

      }
    }
  }
  
    .campaign-link-icon-reverse {
      color: ${backgroundPage};
      background-color: ${headerText};
      border: 1px solid ${backgroundPage};
      &:hover {
        color: ${headerText};
        background-color: ${backgroundPage};
        path, circle, rect {
          stroke: ${backgroundPage};
        }
      }
    }

    .navbar-campaign-link-icon-reverse {
      color: ${backgroundPage};
      background-color: ${headerText};
      border: 1px solid ${backgroundPage};
      &:hover {
        color: ${backgroundPage};
        background-color: ${headerText};
        path, circle, rect {
          stroke: ${backgroundPage};
        }
      }
    }
    .drop-down-item {
      &:hover {
        path, circle, rect, polyline,line {
          stroke:#000000;
        }
        color: #000000;
      }
    }
    .campaign-drop-down-item {
      &:hover {
        path, circle, rect, polyline,line {
          stroke: ${brandDefault};
        }
        color: ${brandDefault};
      }
    }

    .active-link {
      position:relative;
      &:visited {
        color: ${brandDefault};
      }
      &:hover {
        color: ${brandDefault};
      }
      &:after {
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: ${brandDefault}
      }
    }

    .active-link-sub-navbar {
      position:relative;
      &:visited {
        color: ${brandDefault};
      }
      &:hover {
        color: ${brandDefault};
      }
      &:after {
        content: '';
        position:absolute;
        bottom:0;
        left:0;
        width: 100%;
        height: 2px;
        background: ${brandDefault}
      }
    }

    .gl-dots{
      color: gray
      .slick-active {
        span {
          color : ${brandDefault};
        }
      }
    }
    .level-active{
      g{
        g{
          use{
            fill: ${brandDefault}
          }
        }
      }
    }
    .level-next-level{
      g{
        g{
          use{
            fill: ${brandHover}
          }
        }
      }
    }
    .level-locked{
      g{
        g{
          use{
            fill:gray
          }
        }
      }
    }
    .gl-custom-select{
      &__option:hover {
        color: ${textTertiary};
        background: ${brandDefault};
      }
      &__option--is-selected, &__option--is-focused{
        color: ${textTertiary};
        background: ${brandDefault};
        opacity: 0.8;
      }

      &__menu, __menu-list {
        width: calc(100% - 1rem)
      }

      &__option {
        width: calc(100% - 10px);
        margin: 0 5px;
        cursor: pointer;
      }
    }


    .gl-rankings {
      &__data {
        @extend %dead-grey;
        border-top: 11px solid #ffffff;

        &-separator {
          border-color: ${brandDefault};
          border-width: 1px;
          width: 100%;

          &.red {
            border-color: #ff6752;
          }
        }
      }
    }




  `
  }}
`
export default ClassBuilder
